import React from 'react'
import { DcButton, PixelSpinner } from 'pixel-base'
import styled from 'styled-components'
import {
  deleteJournalEntry,
  postJournalEntry,
  reverseJournalEntry,
} from 'utils/api-routes-accounts'
import FinanceStatusBadge from '../../../components/FinanceStatusBadge'
import formatFinanceDate from '../../../utils/formatFinanceDate'

const Header = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 20px;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding: 0 20px;
  margin-bottom: 20px;
`

const Card = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
`

const Label = styled.div`
  color: #6b7280;
  font-size: 12px;
  margin-bottom: 4px;
`

const Value = styled.div`
  color: #111827;
  font-size: 14px;
  font-weight: 600;
`

const Table = styled.div`
  padding: 0 20px 20px;
`

const SummaryRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 12px 0 20px;
  font-weight: 600;
`

interface JournalEntryDetailsProps {
  entry: any
  onClose: () => void
  isLoading: boolean
  onRefresh?: () => void
}

const JournalEntryDetails: React.FC<JournalEntryDetailsProps> = ({
  entry,
  onClose,
  isLoading,
  onRefresh,
}) => {
  if (isLoading) {
    return <PixelSpinner isShow={true} />
  }

  const status = entry.status || entry.state || 'draft'
  const isDraft = status === 'draft'
  const totalDebit = Number(
    entry.total_debit ??
      (entry.lines || []).reduce(
        (sum, line) => sum + Number(line.debit_base ?? line.debit ?? 0),
        0,
      ),
  )
  const totalCredit = Number(
    entry.total_credit ??
      (entry.lines || []).reduce(
        (sum, line) => sum + Number(line.credit_base ?? line.credit ?? 0),
        0,
      ),
  )

  const runAction = (request) => {
    request.subscribe({
      next: () => onRefresh?.(),
    })
  }

  return (
    <>
      <Header>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ color: '#6b7280', fontSize: 13 }}>Journal Entry</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>
              {entry.move_number || entry.name || `Entry #${entry.id}`}
            </div>
          </div>
          <FinanceStatusBadge status={status} />
        </div>
      </Header>
      <Grid>
        <Card>
          <Label>Journal</Label>
          <Value>
            {entry.journal?.code
              ? `${entry.journal.code} - ${entry.journal.name}`
              : entry.journal?.name || '--'}
          </Value>
        </Card>
        <Card>
          <Label>Reference</Label>
          <Value>{entry.reference || '--'}</Value>
        </Card>
        <Card>
          <Label>Document Date</Label>
          <Value>{formatFinanceDate(entry.document_date || entry.date)}</Value>
        </Card>
        <Card>
          <Label>Posting Date</Label>
          <Value>{formatFinanceDate(entry.posting_date)}</Value>
        </Card>
        <Card>
          <Label>Description</Label>
          <Value>{entry.description || entry.name || '--'}</Value>
        </Card>
        <Card>
          <Label>Version</Label>
          <Value>{entry.version_no || '--'}</Value>
        </Card>
        <Card>
          <Label>Total Debit</Label>
          <Value>{totalDebit.toFixed(2)}</Value>
        </Card>
        <Card>
          <Label>Total Credit</Label>
          <Value>{totalCredit.toFixed(2)}</Value>
        </Card>
        <Card>
          <Label>Posted By</Label>
          <Value>{entry.posted_by || '--'}</Value>
        </Card>
      </Grid>
      <Table>
        <div style={{ fontWeight: 700, marginBottom: 12 }}>Lines</div>
        <div style={{ display: 'grid', gap: 8 }}>
          {(entry.lines || []).map((line) => (
            <Card key={line.id || `${line.account_id}-${line.line_no}`}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                  gap: 12,
                }}
              >
                <div>
                  <Label>Account</Label>
                  <Value>
                    {line.account?.code || line.account_id || '--'} -{' '}
                    {line.account?.name || '--'}
                  </Value>
                </div>
                <div>
                  <Label>Partner</Label>
                  <Value>{line.partner_id || '--'}</Value>
                </div>
                <div>
                  <Label>Cost Center</Label>
                  <Value>{line.cost_center_id || '--'}</Value>
                </div>
                <div>
                  <Label>Debit</Label>
                  <Value>{line.debit_base || line.debit || 0}</Value>
                </div>
                <div>
                  <Label>Credit</Label>
                  <Value>{line.credit_base || line.credit || 0}</Value>
                </div>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 2fr',
                  gap: 12,
                  marginTop: 12,
                }}
              >
                <div>
                  <Label>Due Date</Label>
                  <Value>{formatFinanceDate(line.due_date)}</Value>
                </div>
                <div>
                  <Label>Reference</Label>
                  <Value>{line.reference || '--'}</Value>
                </div>
                <div>
                  <Label>Description</Label>
                  <Value>{line.description || '--'}</Value>
                </div>
              </div>
            </Card>
          ))}
        </div>
        <SummaryRow>
          <span>Lines: {entry.line_count || (entry.lines || []).length}</span>
          <span>Total Debit: {totalDebit.toFixed(2)}</span>
          <span>Total Credit: {totalCredit.toFixed(2)}</span>
        </SummaryRow>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 20,
          }}
        >
          <DcButton variant="outline-secondary" onClick={onClose}>
            Close
          </DcButton>
          <div style={{ display: 'flex', gap: 8 }}>
            {isDraft && (
              <>
                <DcButton
                  variant="outline-danger"
                  onClick={() => runAction(deleteJournalEntry(entry.id))}
                >
                  Delete Draft
                </DcButton>
                <DcButton
                  variant="outline-success"
                  onClick={() =>
                    runAction(
                      postJournalEntry(entry.id, {
                        idempotency_key: `post-${entry.id}`,
                      }),
                    )
                  }
                >
                  Post Entry
                </DcButton>
              </>
            )}
            {!isDraft && (
              <DcButton
                variant="outline-warning"
                onClick={() =>
                  runAction(
                    reverseJournalEntry(entry.id, {
                      idempotency_key: `reverse-${entry.id}`,
                    }),
                  )
                }
              >
                Reverse Entry
              </DcButton>
            )}
          </div>
        </div>
      </Table>
    </>
  )
}

export default JournalEntryDetails
